import { Component, OnInit, ChangeDetectionStrategy, ViewEncapsulation, Inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { Observable, debounceTime, map, switchMap } from 'rxjs';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { TsSnackbarService } from 'tsui';
import { Company } from '../../companies.types';
import { RelatedToType } from 'app/shared/enums';
import { CompaniesService } from '../../companies.service';
import { MergeEntityPayload } from 'app/modules/shared-resources/shared-resources.types';
import { SharedResourcesService } from 'app/modules/shared-resources/shared-resources.service';

@Component({
  selector: 'app-merge-company',
  templateUrl: './merge-company.component.html',
  styleUrls: ['./merge-company.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class MergeCompanyComponent extends OnDestroyMixin implements OnInit {
  form: UntypedFormGroup;
  companySearchResults$: Observable<any>;
  sourceCompany: Company;

  get destinationCompany(): Company {
    return this.data.company;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { company: Company },
    private readonly matDialogRef: MatDialogRef<MergeCompanyComponent>,
    private readonly snackBar: TsSnackbarService,
    private readonly sharedResourcesService: SharedResourcesService,
    private readonly companiesService: CompaniesService,
  ) {
    super();
    this.form = new UntypedFormGroup({
      targetCompany: new UntypedFormControl(''),
    });
  }

  ngOnInit(): void {
    this.companySearchResults$ = this.form.get('targetCompany').valueChanges.pipe(
      untilComponentDestroyed(this),
      debounceTime(300),
      switchMap((value: string) => {
        return this.companiesService.getCompanies(1, 5, 'first_name', 'asc', value, {}, false, true);
      }),
      map((value) => value.companies.filter(({ id }) => id !== this.destinationCompany.id)),
    );
  }

  selectedCompany(event: MatAutocompleteSelectedEvent): void {
    this.sourceCompany = event.option.value;
    this.form.get('targetCompany').setValue(this.sourceCompany.name);
  }

  discard(): void {
    this.matDialogRef.close();
  }

  saveAndClose(): void {
    const payload: MergeEntityPayload = {
      type: RelatedToType.Company,
      source_id: this.sourceCompany.id,
      destination_id: this.destinationCompany.id,
    };

    this.sharedResourcesService
      .merge(payload)
      .pipe(untilComponentDestroyed(this))
      .subscribe((result: HttpErrorResponse | any) => {
        if (result instanceof HttpErrorResponse) {
          this.snackBar.open('Error while merging.', 'error');
        } else {
          this.snackBar.open('Companies merged successfully.', 'success');
          this.matDialogRef.close(payload);
        }
      });
  }
}
